import { router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, Minus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useInitials } from "@/hooks/use-initials"
import {
  emvDeltaDirection,
  formatEmvDeltaUsd,
  formatEmvUsd,
} from "@/lib/format-emv"
import { cn } from "@/lib/utils"
import { brandAnalyticsEmvPath } from "@/routes"
import type {
  EmvPagination,
  EmvPostRow,
  EmvPostsPage,
  EmvRange,
} from "@/types/emv-analytics"

/**
 * OKS-503 — Paginated per-post EMV table.
 *
 * Sibling of `AnalyticsCreatorsTable` (OKS-63). One row per tracked post:
 * Creator (avatar + handle), Mission (title), Latest EMV (full USD,
 * cumulative-to-date), Day-over-day Δ (signed USD + ▲/▼ glyph, or "—"
 * on the first-day edge). Sorted EMV-desc (server-side). Paginated via
 * the Inertia partial reload `only: ['posts_page']`, preserving the
 * active `range`. Mobile reflow: stacked cards with EMV emphasized.
 */
export interface EmvPostsTableProps {
  postsPage: EmvPostsPage
  /** Active range — threaded into the pagination links so it survives paging. */
  range: EmvRange
  className?: string
}

export function EmvPostsTable({
  postsPage,
  range,
  className,
}: EmvPostsTableProps) {
  const { t } = useTranslation()
  const isEmpty = postsPage.rows.length === 0

  return (
    <Card
      data-testid="emv-posts-table"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("emv.posts.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <DesktopTable rows={postsPage.rows} className="hidden md:block" />
            <MobileCards rows={postsPage.rows} className="md:hidden" />
            <Pagination
              pagination={postsPage.pagination}
              range={range}
              className="mt-4"
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface RowsProps {
  rows: EmvPostRow[]
  className?: string
}

function DesktopTable({ rows, className }: RowsProps) {
  const { t, i18n } = useTranslation()
  return (
    <div className={className} data-testid="emv-posts-table-desktop">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("emv.posts.columns.creator")}</TableHead>
            <TableHead>{t("emv.posts.columns.mission")}</TableHead>
            <TableHead className="text-right">
              {t("emv.posts.columns.latest_emv")}
            </TableHead>
            <TableHead className="text-right">
              {t("emv.posts.columns.dod")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.submission_id} data-testid="emv-posts-row">
              <TableCell>
                <CreatorCell row={row} />
              </TableCell>
              <TableCell
                className="max-w-[24ch] truncate"
                title={row.mission_title}
              >
                {row.mission_title}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatEmvUsd(row.latest_emv_cents, i18n.language)}
              </TableCell>
              <TableCell className="text-right">
                <DeltaCell row={row} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function MobileCards({ rows, className }: RowsProps) {
  const { i18n } = useTranslation()
  return (
    <ul
      className={cn("flex flex-col gap-3", className)}
      data-testid="emv-posts-table-mobile-list"
    >
      {rows.map((row) => (
        <li
          key={row.submission_id}
          className="bg-muted/30 space-y-2 rounded-lg border p-3"
          data-testid="emv-posts-card"
        >
          <div className="flex items-start justify-between gap-2">
            <CreatorCell row={row} />
            <span className="text-foreground shrink-0 font-semibold">
              {formatEmvUsd(row.latest_emv_cents, i18n.language)}
            </span>
          </div>
          <div className="text-muted-foreground flex items-center justify-between text-xs">
            <span className="max-w-[20ch] truncate" title={row.mission_title}>
              {row.mission_title}
            </span>
            <DeltaCell row={row} />
          </div>
        </li>
      ))}
    </ul>
  )
}

function CreatorCell({ row }: { row: EmvPostRow }) {
  const { t } = useTranslation()
  const getInitials = useInitials()
  const handle = row.creator_handle

  return (
    <div className="flex items-center gap-2">
      <Avatar className="size-7 overflow-hidden rounded-full">
        <AvatarImage
          src={row.creator_avatar_url ?? undefined}
          alt={handle ?? ""}
        />
        <AvatarFallback className="bg-muted text-xs">
          {handle ? getInitials(handle) : "—"}
        </AvatarFallback>
      </Avatar>
      {handle ? (
        <a
          href={`https://www.tiktok.com/@${handle}`}
          target="_blank"
          rel="noreferrer noopener"
          className="text-primary font-medium hover:underline"
        >
          @{handle}
        </a>
      ) : (
        <span className="text-muted-foreground">
          {t("emv.posts.handle_missing")}
        </span>
      )}
    </div>
  )
}

function DeltaCell({ row }: { row: EmvPostRow }) {
  const { i18n } = useTranslation()
  const firstDay = row.dod_first_day || row.dod_cents === null
  const direction = emvDeltaDirection(row.dod_cents)
  const display = formatEmvDeltaUsd(row.dod_cents, i18n.language)

  const Icon = firstDay
    ? Minus
    : direction === "up"
      ? ArrowUp
      : direction === "down"
        ? ArrowDown
        : Minus

  const tone =
    firstDay || direction === "flat"
      ? "text-muted-foreground"
      : direction === "up"
        ? "text-emerald-600 dark:text-emerald-400"
        : "text-destructive"

  return (
    <span
      className={cn("inline-flex items-center justify-end gap-1", tone)}
      data-testid="emv-posts-dod"
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {display}
    </span>
  )
}

const POSTS_RELOAD_ONLY = ["posts_page"]

function Pagination({
  pagination,
  range,
  className,
}: {
  pagination: EmvPagination
  range: EmvRange
  className?: string
}) {
  const { t } = useTranslation()
  if (pagination.pages <= 1) return null

  const goto = (page: number) => {
    router.get(
      brandAnalyticsEmvPath({ range, page }),
      {},
      {
        only: POSTS_RELOAD_ONLY,
        preserveState: false,
        preserveScroll: true,
      },
    )
  }

  return (
    <nav
      className={cn("flex items-center justify-between gap-2", className)}
      data-testid="emv-posts-pagination"
      aria-label={t("emv.posts.pagination_aria")}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={pagination.prev == null}
        onClick={() => pagination.prev != null && goto(pagination.prev)}
        data-testid="emv-posts-pagination-prev"
      >
        ←
      </Button>
      <span className="text-muted-foreground text-sm">
        {pagination.page} / {pagination.pages}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={pagination.next == null}
        onClick={() => pagination.next != null && goto(pagination.next)}
        data-testid="emv-posts-pagination-next"
      >
        →
      </Button>
    </nav>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="emv-posts-table-empty"
      className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm"
    >
      <p className="text-foreground text-sm font-medium">
        {t("emv.posts.empty.title")}
      </p>
      <p>{t("emv.posts.empty.body")}</p>
    </div>
  )
}
